import { Hardware_Vendor } from '@occc/graphql';

import { StatusDotVariant } from './status-dot.component';

export type ConnectionStatus = {
  variant: StatusDotVariant;
  label: string;
};

// Scan data is submitted on a weekly cadence. Within the warning threshold it's
// still considered current; beyond the stale threshold it's treated as not submitting.
const SCAN_DATA_WARNING_THRESHOLD_DAYS = 8;
const SCAN_DATA_STALE_THRESHOLD_DAYS = 14;

// Mirrors oc-retailer-connect's `siteOnlineTimeInterval` default (src/config/config.ts),
// which is how this same domain defines "online" elsewhere for mobile/loyalty heartbeats.
const MOBILE_LOYALTY_ONLINE_THRESHOLD_SECONDS = 180;

export const HARDWARE_VENDOR_LABELS: Record<Hardware_Vendor, string> = {
  [Hardware_Vendor.Comdata]: 'Comdata',
  [Hardware_Vendor.Gilbarco]: 'Gilbarco',
  [Hardware_Vendor.Mashgin]: 'Mashgin',
  [Hardware_Vendor.Ncr]: 'NCR',
  [Hardware_Vendor.NcrPes]: 'NCR PES',
  [Hardware_Vendor.Stuzo]: 'Stuzo',
  [Hardware_Vendor.Verifone]: 'Verifone',
};

// Per the OCST-16037 tech spec: when a system is disabled, show a neutral
// "Not Connected" dot rather than treating it as an outage. Otherwise, status is
// based on the recency of the last connection — a stale connection is still
// "Offline" but keeps showing its last-known timestamp rather than hiding it.
export const getConnectionStatus = (
  enabled?: boolean | null,
  connectedAt?: string | null,
  now: Date = new Date(),
): ConnectionStatus => {
  if (!enabled) {
    return { variant: 'default', label: 'Not Connected' };
  }

  if (!connectedAt) {
    return { variant: 'error', label: 'Offline' };
  }

  const secondsSinceLastConnection = (now.getTime() - new Date(connectedAt).getTime()) / 1000;

  return secondsSinceLastConnection <= MOBILE_LOYALTY_ONLINE_THRESHOLD_SECONDS
    ? { variant: 'success', label: 'Online' }
    : { variant: 'error', label: 'Offline' };
};

export const getScanDataStatus = (
  scanDataSubmittedAt?: string | null,
  now: Date = new Date(),
): ConnectionStatus => {
  if (!scanDataSubmittedAt) {
    return { variant: 'default', label: 'Not Submitting' };
  }

  const daysSinceLastSubmission =
    (now.getTime() - new Date(scanDataSubmittedAt).getTime()) / (1000 * 60 * 60 * 24);

  if (daysSinceLastSubmission <= SCAN_DATA_WARNING_THRESHOLD_DAYS) {
    return { variant: 'success', label: 'Submitted' };
  }

  // Beyond the stale threshold the store has a genuine problem — surface it in red rather than
  // the neutral grey, which is reserved for the "never submitted / no data" state.
  return daysSinceLastSubmission <= SCAN_DATA_STALE_THRESHOLD_DAYS
    ? { variant: 'warning', label: 'Submitted' }
    : { variant: 'error', label: 'Submitted' };
};
